import React, { useRef, useMemo, useEffect, useCallback } from 'react';

import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import PropTypes from 'prop-types';

import { Flex } from '@adsk/alloy-react-box';
import { keyBy } from '@adsk/alloy-react-helpers';
import {
  useTable,
  TableHeaderGroup,
  TableHeader,
  TableRow,
  TableCell,
  TABLE_SIZES,
  DEFAULT_TABLE_SIZE,
} from '@adsk/alloy-react-table';

import TableFooter from './TableFooter';
import {
  getSelectionColumn,
  getTableCellStyle,
  getTableHeaderStyle,
  selectTableRows,
} from '../helpers/utils';

const Table = ({
  multiple,
  enableColumnResizing = false,
  entities: data,
  columns: tableCols,
  selectedIds = [],
  disabledIds = [],
  tableSize = DEFAULT_TABLE_SIZE,
  reachEndThreshold = 200,
  loading,
  loadingMoreText,
  error,
  errorText,
  hideHeader = false,
  setSelection,
  onChange,
  onLoadMore,
  onTryAgain,
  ...rest
}) => {
  const tableRef = useRef(null);
  // Need a ref to save the value instead of a local var inside getSelectionColumn
  // as the function will be executed multiple times under some cases.
  const lastSelectRowIdxRef = useRef(null);
  const scrollProps = useRef({
    lastScrollTop: 0,
    lastScrollHeight: 0,
    dataChanged: true,
  });

  // Prefix selection column as shared config
  const columns = useMemo(
    () => [getSelectionColumn(), ...tableCols],
    [tableCols],
  );
  const rowSelection = useMemo(
    () =>
      selectedIds.reduce((pre, id) => {
        pre[id] = true;
        return pre;
      }, {}),
    [selectedIds],
  );

  const dataByKey = useMemo(() => keyBy(data, 'id'), [data]);
  const onRowSelectionChange = useCallback(
    (updaterOrValue) => {
      const curSelection =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(rowSelection)
          : updaterOrValue;

      const preKeys = Object.keys(rowSelection);
      const curKeys = Object.keys(curSelection);

      const added = curKeys
        .filter((id) => !preKeys.includes(id))
        .map((id) => dataByKey[id]);
      const removed = preKeys
        .filter((id) => !curKeys.includes(id))
        .map((id) => dataByKey[id]);

      // /////////////////////////////////////////////////////////////////////////////////////// //
      // setSelection vs onChange:
      //  - setSelection will batch set selected entities.
      //  - onChange will set selected entities one by one.
      //  (Refer to the implementation at default export of hooks/useEntitySelection.js. onSelectionChange & setSelection)
      //
      // In details, we have to consider the following cases:
      // - If curKeys is empty, it means selectable rows are cleared (E.g. unchecked all rows at header checkbox).
      //    We can simply to call setSelection to reset the selection status.
      // - Once any row is removed, we have to call onChange to reset the selection status for it.
      // - For other cases, we can call onChange to update the selection status.
      // /////////////////////////////////////////////////////////////////////////////////////// //
      if (curKeys.length === 0) {
        setSelection([]);
      } else {
        removed.forEach((row) => {
          onChange({ entity: row, checked: false });
        });
        added.forEach((row) => {
          onChange({ entity: row, checked: true });
        });
      }
    },
    [rowSelection, dataByKey, onChange, setSelection],
  );

  const table = useReactTable({
    columns,
    data,
    state: {
      rowSelection,
    },
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing,
    enableSorting: false,
    enableRowSelection: (row) => !disabledIds.includes(row.id),
    enableMultiRowSelection: multiple,
    columnResizeMode: 'onChange',
    onRowSelectionChange,
  });

  const {
    getTableHeaderProps,
    getTableHeaderGroupProps,
    getTableRowProps,
    getTableCellProps,
  } = useTable({
    table,
    tableSize,
    enableFullRowClick: true,
  });

  // Virtualizer
  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableRef.current,
    estimateSize: () => tableSize.rowHeight || 48,
    overscan: 3,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const virtualHeight = rowVirtualizer.getTotalSize();
  const layouts = useMemo(
    () => ({
      paddingTop: virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0,
      paddingBottom:
        virtualRows.length > 0
          ? virtualHeight - (virtualRows?.[virtualRows.length - 1]?.end || 0)
          : 0,
    }),
    [virtualRows, virtualHeight],
  );

  const checkLoadMore = () => {
    // No need to check if no onLoadMore, no rows or no tableRef
    if (!onLoadMore || !rows || !tableRef.current) return;

    // No need to check if under loading or error state
    if ((loading || error) && rows.length > 0) return;

    const scrollHeight = tableRef.current.scrollHeight;
    const clientHeight = tableRef.current.clientHeight;
    const scrollTop = tableRef.current.scrollTop;
    const distanceToEnd =
      scrollHeight - scrollTop - clientHeight - reachEndThreshold;
    const { lastScrollHeight, dataChanged } = scrollProps.current;

    if (
      distanceToEnd <= 0 &&
      (dataChanged || scrollHeight !== lastScrollHeight)
    ) {
      scrollProps.current.dataChanged = false;
      scrollProps.current.lastScrollHeight = scrollHeight;
      onLoadMore();
    }
  };

  const handleScroll = () => {
    if (!tableRef.current) return;
    const scrollTop = tableRef.current.scrollTop;
    if (scrollTop > scrollProps.current.lastScrollTop) checkLoadMore();
    scrollProps.current.lastScrollTop = scrollTop;
  };

  const handleRowSelected = useCallback(
    (row, val, e) => {
      const isShiftSelect = e.shiftKey || e.nativeEvent?.shiftKey;
      selectTableRows({
        val,
        multiple,
        isShiftSelect,
        table,
        row,
        lastSelectRowIdxRef,
      });
    },
    [multiple, table],
  );

  useEffect(() => {
    scrollProps.current.dataChanged = true;
  }, [data]);

  return (
    <Flex
      position="relative"
      width="100%"
      height="100%"
      overflow="auto"
      flexDirection="column"
      {...rest}
      onScroll={handleScroll}
      ref={tableRef}
    >
      <table
        style={{
          // fixes sticky header jumping: https://stackoverflow.com/questions/70772981/table-sticky-header-jumps
          borderSpacing: 0,
        }}
      >
        {!hideHeader && (
          <thead style={{ position: 'sticky', margin: 0, top: 0, zIndex: 1 }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderGroup
                key={headerGroup.id}
                {...getTableHeaderGroupProps(headerGroup)}
              >
                {headerGroup.headers.map((header) => (
                  <TableHeader
                    key={header.id}
                    {...getTableHeaderProps(header)}
                    {...getTableHeaderStyle(header)}
                  />
                ))}
              </TableHeaderGroup>
            ))}
          </thead>
        )}
        <tbody>
          {layouts.paddingTop > 0 && (
            <tr>
              <td style={{ height: `${layouts.paddingTop}px` }} />
            </tr>
          )}
          {virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index];
            const cells = row.getVisibleCells();
            return (
              <TableRow
                key={row.id}
                {...getTableRowProps(row)}
                toggleSelected={(...args) => handleRowSelected(row, ...args)}
              >
                {cells.map((cell) => (
                  <TableCell
                    key={cell.id}
                    {...getTableCellProps(cell)}
                    {...getTableCellStyle(cell)}
                  />
                ))}
              </TableRow>
            );
          })}
          {layouts.paddingBottom > 0 && (
            <tr>
              <td style={{ height: `${layouts.paddingBottom}px` }} />
            </tr>
          )}
        </tbody>
      </table>
      <TableFooter
        loading={loading}
        error={error}
        loadingMoreText={loadingMoreText}
        errorText={errorText}
        onRetry={onTryAgain}
      />
    </Flex>
  );
};

Table.propTypes = {
  /** Whether to use multi select or not */
  multiple: PropTypes.bool,
  /** Whether to enable column resizing */
  enableColumnResizing: PropTypes.bool,
  /** Array of entities */
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  ),
  /** Array of table columns */
  columns: PropTypes.array,
  /** Selected row ids */
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  /** Disabled row ids */
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  /** Whether is loading more data */
  loading: PropTypes.bool,
  /** Whether failed to load more data */
  error: PropTypes.bool,
  /** Loading more text */
  loadingMoreText: PropTypes.node,
  /** Error text */
  errorText: PropTypes.node,
  /** The table row size which is LARGE, MEDIUM or SMALL */
  tableSize: PropTypes.oneOf(Object.values(TABLE_SIZES)),
  /** Threshold in pixels for calling `onLoadMore` */
  reachEndThreshold: PropTypes.number,
  /** Whether hide the table header */
  hideHeader: PropTypes.bool,
  /**
   * Callback to load more data, with the shape of () => any
   */
  onLoadMore: PropTypes.func,
  /**
   * Callback to reload failed batch, with the shape of () => any
   **/
  onTryAgain: PropTypes.func,
  /**
   * Callback when user toggle single row selection, with the shape of ({ entity, checked }) => any
   **/
  onChange: PropTypes.func,
  /**
   * Callback to setup table selection with the shape of (entities: []) => any
   **/
  setSelection: PropTypes.func,
};

export { Table as default, TABLE_SIZES };
